import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  Edit,
} from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import ChatbotModal from "../ChatbotModal";
import MessageBubblePreview from "../MessageBubblePreview";
import {
  buildMessageVariables,
  fetchContactCustomFieldNames,
  fetchWasheetMessageVariables,
} from "../../utils/messageVariables";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

const NO_SEQUENCE = "no-sequence";
const NO_RECURRENCE_UNIT = "__no_recurrence_unit__";

const useStyles = makeStyles(theme => ({
  headerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  textField: {
    width: "100%",
  },
  reminderRow: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
  },
  rowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  rowActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  formBox: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fafafa",
  },
  block: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fff",
  },
  blockHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  blockActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  blockGrid: {
    display: "grid",
    gridTemplateColumns: "330px minmax(0, 1fr) 90px",
    gap: theme.spacing(1),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "290px minmax(0, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageDelayField: {
    [theme.breakpoints.down("sm")]: {
      gridColumn: "2",
    },
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
    },
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
  },
  helper: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  variablesBox: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fff",
  },
  variablesLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  variablesChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  emptyText: {
    color: theme.palette.text.secondary,
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
}));

const acceptedMedia =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar,.webp";

const createBlock = () => ({
  text: "",
  delaySeconds: 0,
  file: null,
  media: null,
});

const initialForm = {
  id: null,
  title: "",
  actionType: "message",
  date: "",
  time: "",
  recurrenceValue: "",
  recurrenceUnit: "",
  chatbotSequenceId: "",
};

const pad = value => String(value).padStart(2, "0");

const toLocalParts = value => {
  if (!value) return { date: "", time: "" };
  const date = new Date(value);
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const formatSequenceOption = sequence =>
  `ID: ${sequence.id} - ${sequence.name}`;

const TicketRemindersModal = ({ modalOpen, onClose, ticket }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [blocks, setBlocks] = useState([createBlock()]);
  const [deletingReminder, setDeletingReminder] = useState(null);
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [washeetVariables, setWasheetVariables] = useState([]);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const messageVariables = buildMessageVariables(customFieldNames, washeetVariables);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const resetForm = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    setForm({
      ...initialForm,
      ...toLocalParts(now),
    });
    setBlocks([createBlock()]);
    setShowForm(false);
  };

  const fetchData = async () => {
    if (!modalOpen || !ticket?.id) return;

    setLoading(true);
    try {
      const [
        { data: reminderData },
        { data: sequenceData },
        nextCustomFieldNames,
        nextWasheetVariables,
      ] = await Promise.all([
        api.get(`/tickets/${ticket.id}/reminders`),
        api.get("/chatbot/sequences", {
          params: {
            all: true,
            pageNumber: 1,
            searchParam: "",
            connectionId: ticket.connectionId,
          },
        }),
        fetchContactCustomFieldNames(),
        fetchWasheetMessageVariables(),
      ]);

      if (isMounted.current) {
        setReminders(reminderData.reminders || []);
        setSequences(sequenceData.sequences || []);
        setCustomFieldNames(nextCustomFieldNames);
        setWasheetVariables(nextWasheetVariables);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen) {
      resetForm();
      fetchData();
    }
  }, [modalOpen, ticket]);

  const updateBlock = (index, patch) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...patch } : block
      )
    );
  };

  const moveBlock = (index, direction) => {
    setBlocks(prevBlocks => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prevBlocks.length) return prevBlocks;

      const nextBlocks = [...prevBlocks];
      const current = nextBlocks[index];
      nextBlocks[index] = nextBlocks[nextIndex];
      nextBlocks[nextIndex] = current;
      return nextBlocks;
    });
  };

  const removeBlock = index => {
    setBlocks(prevBlocks =>
      prevBlocks.length === 1
        ? [createBlock()]
        : prevBlocks.filter((_, blockIndex) => blockIndex !== index)
    );
  };

  const upsertSequence = sequence => {
    if (!sequence?.id) return;
    setSequences(prevSequences => {
      const exists = prevSequences.some(item => String(item.id) === String(sequence.id));
      return exists
        ? prevSequences.map(item => (String(item.id) === String(sequence.id) ? sequence : item))
        : [...prevSequences, sequence];
    });
  };

  const openCreateSequence = () => {
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = sequenceId => {
    if (!sequenceId || sequenceId === NO_SEQUENCE) return;
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    upsertSequence(savedSequence);
    if (savedSequenceId) {
      setForm(prevForm => ({
        ...prevForm,
        chatbotSequenceId: String(savedSequenceId),
      }));
    }
    setSequenceModalOpen(false);
    setSequenceModalId(null);
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
  };

  const addVariableToBlock = (index, variable) => {
    setBlocks(prevBlocks =>
      prevBlocks.map((block, blockIndex) => {
        if (blockIndex !== index) return block;
        const separator = block.text?.trim() ? " " : "";
        return { ...block, text: `${block.text || ""}${separator}${variable}` };
      })
    );
  };

  const handleEdit = reminder => {
    setForm({
      id: reminder.id,
      title: reminder.title || "",
      actionType: reminder.actionType || "message",
      ...toLocalParts(reminder.scheduledAt || reminder.nextRunAt),
      recurrenceValue: reminder.recurrenceValue || "",
      recurrenceUnit: reminder.recurrenceUnit || "",
      chatbotSequenceId: reminder.chatbotSequenceId || "",
    });
    setBlocks(
      reminder.blocks?.length
        ? reminder.blocks.map(block => ({
            text: block.text || "",
            delaySeconds: Number(block.delaySeconds || 0),
            file: null,
            media: block.media || null,
          }))
        : [createBlock()]
    );
    setShowForm(true);
  };

  const buildFormData = () => {
    const normalizedBlocks = blocks.map(block => ({
      text: block.text || "",
      delaySeconds: Number(block.delaySeconds || 0),
      media: block.file ? undefined : block.media,
    }));
    const scheduledAt = new Date(`${form.date}T${form.time}:00`);
    const formData = new FormData();

    formData.append("title", form.title);
    formData.append("actionType", form.actionType);
    formData.append("scheduledAt", scheduledAt.toISOString());
    formData.append("recurrenceValue", form.recurrenceValue || "");
    formData.append("recurrenceUnit", form.recurrenceUnit || "");
    formData.append("chatbotSequenceId", form.chatbotSequenceId || "");
    formData.append("blocks", JSON.stringify(normalizedBlocks));

    blocks.forEach((block, index) => {
      if (block.file) {
        formData.append(`blockFile_${index}`, block.file);
      }
    });

    return formData;
  };

  const validateForm = () => {
    if (!form.date || !form.time) {
      toast.error(i18n.t("ticketRemindersModal.validation.datetime"));
      return false;
    }

    if (form.recurrenceUnit && !Number(form.recurrenceValue)) {
      toast.error(i18n.t("ticketRemindersModal.validation.recurrence"));
      return false;
    }

    if (form.actionType === "sequence" && !form.chatbotSequenceId) {
      toast.error(i18n.t("ticketRemindersModal.validation.sequence"));
      return false;
    }

    if (
      form.actionType === "message" &&
      !blocks.some(block => block.text.trim() || block.file || block.media)
    ) {
      toast.error(i18n.t("ticketRemindersModal.validation.message"));
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const formData = buildFormData();
      const config = { headers: { "Content-Type": "multipart/form-data" } };

      if (form.id) {
        await api.put(`/reminders/${form.id}`, formData, config);
      } else {
        await api.post(`/tickets/${ticket.id}/reminders`, formData, config);
      }

      toast.success(i18n.t("ticketRemindersModal.toasts.saved"));
      resetForm();
      fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async reminderId => {
    try {
      await api.delete(`/reminders/${reminderId}`);
      toast.success(i18n.t("ticketRemindersModal.toasts.deleted"));
      setDeletingReminder(null);
      fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const formatRunAt = value =>
    value ? new Date(value).toLocaleString() : i18n.t("ticketRemindersModal.values.completed");

  return (
    <>
      <Dialog open={modalOpen} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{i18n.t("ticketRemindersModal.title")}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.headerActions}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
            >
              {privacyCensorSettings.contactNames
                ? "Contacto privado"
                : ticket?.contact?.name || ""}
            </Typography>
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                if (showForm) {
                  resetForm();
                } else {
                  setShowForm(true);
                }
              }}
            >
              {showForm
                ? i18n.t("ticketRemindersModal.buttons.cancel")
                : i18n.t("ticketRemindersModal.buttons.add")}
            </Button>
          </div>

          {showForm && (
            <Paper className={classes.formBox} elevation={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={i18n.t("ticketRemindersModal.form.title")}
                    value={form.title}
                    onChange={event => setForm({ ...form, title: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>{i18n.t("ticketRemindersModal.form.actionType")}</InputLabel>
                    <Select
                      value={form.actionType}
                      label={i18n.t("ticketRemindersModal.form.actionType")}
                      onChange={event => setForm({ ...form, actionType: event.target.value })}
                    >
                      <MenuItem value="message">
                        {i18n.t("ticketRemindersModal.actionTypes.message")}
                      </MenuItem>
                      <MenuItem value="sequence">
                        {i18n.t("ticketRemindersModal.actionTypes.sequence")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={i18n.t("ticketRemindersModal.form.date")}
                    type="date"
                    value={form.date}
                    onChange={event => setForm({ ...form, date: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={i18n.t("ticketRemindersModal.form.time")}
                    type="time"
                    value={form.time}
                    onChange={event => setForm({ ...form, time: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label={i18n.t("ticketRemindersModal.form.recurrenceValue")}
                    type="number"
                    value={form.recurrenceValue}
                    onChange={event =>
                      setForm({ ...form, recurrenceValue: event.target.value })
                    }
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>{i18n.t("ticketRemindersModal.form.recurrenceUnit")}</InputLabel>
                    <Select
                      value={form.recurrenceUnit || NO_RECURRENCE_UNIT}
                      label={i18n.t("ticketRemindersModal.form.recurrenceUnit")}
                      onChange={event =>
                        setForm({
                          ...form,
                          recurrenceUnit:
                            event.target.value === NO_RECURRENCE_UNIT
                              ? ""
                              : event.target.value,
                        })
                      }
                    >
                      <MenuItem value={NO_RECURRENCE_UNIT}>
                        {i18n.t("ticketRemindersModal.recurrenceUnits.none")}
                      </MenuItem>
                      <MenuItem value="minutes">
                        {i18n.t("ticketRemindersModal.recurrenceUnits.minutes")}
                      </MenuItem>
                      <MenuItem value="hours">
                        {i18n.t("ticketRemindersModal.recurrenceUnits.hours")}
                      </MenuItem>
                      <MenuItem value="days">
                        {i18n.t("ticketRemindersModal.recurrenceUnits.days")}
                      </MenuItem>
                      <MenuItem value="weeks">
                        {i18n.t("ticketRemindersModal.recurrenceUnits.weeks")}
                      </MenuItem>
                      <MenuItem value="months">
                        {i18n.t("ticketRemindersModal.recurrenceUnits.months")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {form.actionType === "sequence" ? (
                <>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <InputLabel>{i18n.t("ticketRemindersModal.form.sequence")}</InputLabel>
                    <Select
                      value={form.chatbotSequenceId || NO_SEQUENCE}
                      label={i18n.t("ticketRemindersModal.form.sequence")}
                      onChange={event => {
                        if (event.target.value === "__create__") {
                          openCreateSequence();
                          return;
                        }
                        setForm({
                          ...form,
                          chatbotSequenceId:
                            event.target.value === NO_SEQUENCE ? "" : event.target.value,
                        });
                      }}
                    >
                      <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                      <MenuItem value="__create__">Crear secuencia...</MenuItem>
                      {sequences.map(sequence => (
                        <MenuItem key={sequence.id} value={String(sequence.id)}>
                          {formatSequenceOption(sequence)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <div>
                    <Button
                      color="primary"
                      disabled={!form.chatbotSequenceId}
                      onClick={() => openEditSequence(form.chatbotSequenceId)}
                    >
                      Editar secuencia
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {blocks.map((block, index) => (
                    <Paper className={classes.block} elevation={0} key={`reminder-block-${index}`}>
                      <div className={classes.blockHeader}>
                        <Typography variant="subtitle2">
                          {i18n.t("ticketRemindersModal.form.message")} {index + 1}
                        </Typography>
                        <div className={classes.blockActions}>
                          <IconButton size="small" onClick={() => moveBlock(index, -1)} disabled={index === 0}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}>
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => removeBlock(index)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                      <div className={classes.blockGrid}>
                        <MessageBubblePreview block={block} />
                        <TextField
                          label={i18n.t("ticketRemindersModal.form.message")}
                          value={block.text}
                          onChange={event => updateBlock(index, { text: event.target.value })}
                          variant="outlined"
                          margin="dense"
                          multiline
                          minRows={5}
                          fullWidth
                        />
                        <TextField
                          className={classes.messageDelayField}
                          label={i18n.t("ticketRemindersModal.form.delay")}
                          type="number"
                          value={block.delaySeconds}
                          onChange={event =>
                            updateBlock(index, {
                              delaySeconds: Math.max(0, Number(event.target.value)),
                            })
                          }
                          variant="outlined"
                          margin="dense"
                          helperText={i18n.t("ticketRemindersModal.recurrenceUnits.seconds")}
                          inputProps={{ min: 0, step: 1 }}
                          fullWidth
                        />
                      </div>
                      <div className={classes.variablesBox}>
                        <Typography variant="caption" className={classes.variablesLabel}>
                          Variables disponibles
                        </Typography>
                        <div className={classes.variablesChips}>
                          {messageVariables.map(variable => (
                            <Chip
                              key={`reminder-block-${index}-${variable.value}`}
                              size="small"
                              label={variable.label}
                              onClick={() => addVariableToBlock(index, variable.value)}
                            />
                          ))}
                        </div>
                      </div>
                      <div className={classes.fileRow}>
                        <input
                          accept={acceptedMedia}
                          id={`reminder-file-${index}`}
                          type="file"
                          style={{ display: "none" }}
                          onChange={event =>
                            updateBlock(index, {
                              file: event.target.files?.[0] || null,
                              media: event.target.files?.[0] ? null : block.media,
                            })
                          }
                        />
                        <label htmlFor={`reminder-file-${index}`}>
                          <Button variant="outlined" component="span" size="small">
                            {i18n.t("ticketRemindersModal.buttons.file")}
                          </Button>
                        </label>
                        <Typography variant="body2" className={classes.fileName}>
                          {block.file?.name ||
                            block.media?.originalName ||
                            i18n.t("ticketRemindersModal.form.noFile")}
                        </Typography>
                        {(block.file || block.media) && (
                          <Button size="small" onClick={() => updateBlock(index, { file: null, media: null })}>
                            {i18n.t("ticketRemindersModal.buttons.removeFile")}
                          </Button>
                        )}
                      </div>
                      <small className={classes.helper}>
                        Imagenes, stickers .webp, audios, videos y documentos.
                      </small>
                    </Paper>
                  ))}
                  <Button
                    startIcon={<Add />}
                    color="primary"
                    onClick={() => setBlocks(prevBlocks => [...prevBlocks, createBlock()])}
                    style={{ marginTop: 12 }}
                  >
                    {i18n.t("ticketRemindersModal.buttons.addBlock")}
                  </Button>
                </>
              )}

              <DialogActions>
                <Button onClick={resetForm} color="secondary" variant="outlined" disabled={saving}>
                  {i18n.t("ticketRemindersModal.buttons.cancel")}
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : i18n.t("ticketRemindersModal.buttons.save")}
                </Button>
              </DialogActions>
            </Paper>
          )}

          {loading ? (
            <CircularProgress size={24} />
          ) : reminders.length ? (
            reminders.map(reminder => (
              <Paper className={classes.reminderRow} elevation={0} key={reminder.id}>
                <div className={classes.rowHeader}>
                  <div>
                    <Typography variant="subtitle2">
                      {reminder.title || i18n.t("ticketRemindersModal.values.untitled")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatRunAt(reminder.nextRunAt)} ·{" "}
                      {i18n.t(`ticketRemindersModal.actionTypes.${reminder.actionType}`)} ·{" "}
                      {i18n.t(`ticketRemindersModal.status.${reminder.status}`)}
                    </Typography>
                    {reminder.lastError && (
                      <Typography variant="caption" color="error">
                        {reminder.lastError}
                      </Typography>
                    )}
                  </div>
                  <div className={classes.rowActions}>
                    <IconButton size="small" onClick={() => handleEdit(reminder)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingReminder(reminder)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" className={classes.emptyText}>
              {i18n.t("ticketRemindersModal.empty")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {i18n.t("ticketRemindersModal.buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={ticket?.connectionId}
        initialSequenceName="Recordatorio"
      />

      <ConfirmationModal
        title={i18n.t("ticketRemindersModal.confirmationModal.deleteTitle")}
        open={!!deletingReminder}
        onClose={() => setDeletingReminder(null)}
        onConfirm={() => handleDelete(deletingReminder.id)}
      >
        {i18n.t("ticketRemindersModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>
    </>
  );
};

export default TicketRemindersModal;

